import * as io from "io-ts";
import { isSiteSection } from "../guards/is-site-section";
import { ILog } from "../lib/logging";
import { SubscriptionArrayResolver } from "./PubSub.types";
import { MaybeStringArrayResolver, MaybeStringResolver } from "./util.types";
const log = ILog.logger("Sites.types");
export const ImageRefResolver = io.type({
    id: io.string,
    bucket: io.string,
});
export const CatalogRefResolver = io.type({
    id: io.string,
    kind: io.string,
    country: io.string,
});
export const ScheduleResolver = io.partial({
    begin: io.number,
    end: io.number,
});
const TargetResolver = io.type({
    tags: MaybeStringArrayResolver,
});
export const CollectionQuerySubscriptionResolver = io.partial({
    subscription: SubscriptionArrayResolver,
    tagset: io.string,
    limit: io.number,
    offset: io.number,
});
export const HeaderBlockValueResolver = io.partial({
    background_image: io.union([io.string, io.null]),
    background_color: io.union([io.string, io.null]),
    title: io.union([io.string, io.null]),
    subtitle: io.union([io.string, io.null]),
});
export const CalendarBlockValueResolver = io.partial({
    category: io.union([io.string, io.null]),
    limit: io.number,
    title: io.union([io.string, io.null]),
    schedule: io.union([ScheduleResolver, io.null]),
});
export const PageBlockLinkResolver = io.partial({
    label: io.string,
    ref: io.string,
    target: io.string,
    type: io.string,
});
export const FeedBlockValueResolver = io.partial({
    feed_id: io.string,
    feed_index: io.number,
    title: io.union([io.string, io.null]),
    size: io.number,
    link: io.union([io.array(PageBlockLinkResolver), io.UnknownArray, io.null, io.undefined]),
});
export const ItemBlockValueResolver = io.partial({
    feed_id: io.string,
    feed_index: io.number,
    title: io.union([io.string, io.null]),
    ad_split: io.union([io.number, io.null]),
    position: io.union([io.string, io.null]),
});
export const ContentBlockValueResolver = io.partial({
    id: io.string,
    schedule: io.union([ScheduleResolver, io.null]),
});
export const HtmlBlockValueResolver = io.type({
    content: io.string,
});
export const TwitterBlockValueResolver = io.type({
    handle: io.union([io.string, io.null]),
    use_site_handle: io.union([io.boolean, io.null]),
});
export const TitleBlockValueResolver = io.partial({
    title: io.union([io.string, io.null]),
    description: io.union([io.string, io.null]),
});
export const RecommendationBlockValueResolver = io.type({
    widget_id: io.string,
});
export const ShowResolver = io.type({
    _id: io.string,
    slug: io.string,
    ownBrand: io.string,
    configByLookup: io.type({
        common: io.partial({
            branding: io.partial({
                name: io.string,
                logo: io.partial({
                    asset: io.partial({
                        href: io.string,
                    }),
                }),
            }),
        }),
    }),
});
export const MeetTheShowPersonalityResolver = io.type({
    title: io.string,
    link: io.type({
        urls: io.type({
            web: io.string,
        }),
    }),
    image: io.string,
});
export const HowToListenBlockResolver = io.type({
    showName: io.string,
    tabs: io.type({
        list: io.type({
            display: io.boolean,
            showMap: io.boolean,
            label: io.string,
        }),
        map: io.type({
            showTotalCount: io.boolean,
            label: io.string,
        }),
        online: io.type({
            display: io.boolean,
            label: io.string,
        }),
    }),
    clustering: io.boolean,
    defaultTab: io.string,
    initCoords: io.type({
        lat: io.number,
        lng: io.number,
    }),
    geolocation: io.string,
    startZoom: io.number,
    notFoundMessage: io.union([io.string, io.null]),
    mapStyle: io.union([io.string, io.null]),
});
export const AdsResolver = io.intersection([
    io.type({
        facebook_ad_track_pixel_id: io.union([io.string, io.null]),
        facebook_ad_track_pixel_switch: io.union([io.boolean, io.null]),
        header_bidding: io.union([io.boolean, io.null]),
        interstitial_switch: io.union([io.boolean, io.null]),
    }),
    io.partial({
        dfp_switch: io.union([io.boolean, io.null]),
        simplifi_cid: io.union([io.string, io.null]),
        simplifi_segment: io.union([io.string, io.null]),
        simplifi_sifi_tuid: io.union([io.string, io.null]),
        simplifi_switch: io.union([io.boolean, io.null]),
        tronc_switch: io.union([io.boolean, io.null]),
        yieldmo_switch: io.union([io.boolean, io.null]),
    }),
]);
export const HideResolver = io.partial({
    startDate: io.union([io.number, io.null]),
    endDate: io.union([io.number, io.null]),
    redirectTo: io.union([io.string, io.null]),
});
export const VideoBlockResolver = io.partial({
    title: MaybeStringResolver,
    groupid: MaybeStringResolver,
    episodeid: MaybeStringResolver,
});
export const VideoPlaylistBlockResolver = io.partial({
    year: io.string,
    month: io.string,
    day: io.string,
});
export class Site {
    constructor(site, micrositeReferences = [], surrogateKeys = []) {
        Object.defineProperty(this, "site", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: site
        });
        Object.defineProperty(this, "micrositeReferences", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: micrositeReferences
        });
        Object.defineProperty(this, "surrogateKeys", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: surrogateKeys
        });
        if (!this.sections) {
            throw new Error("No Site Config");
        }
        if (!isSiteSection(this.sections)) {
            const errorMessage = `Section did not validate!`;
            log.error(errorMessage);
        }
        // Backfill general.name from summary
        if (this.sections.general && !this.sections.general.name) {
            this.sections.general.name = this.index.summary.name;
        }
        if (!this.index.stream) {
            this.index.stream = {};
        }
    }
    get index() {
        if (!this.site.sites.find) {
            throw new Error("No IndexRecord");
        }
        return this.site.sites.find;
    }
    get sections() {
        if (!this.config.sections) {
            throw new Error("No Site Config");
        }
        return this.config.sections;
    }
    get config() {
        const config = this.index.configByLookup;
        if (!config) {
            log.debug(`No Config Record for slug: ${this.index.slug}`);
            throw new Error(`No Config Record on Index: ${this.index._id} for slug: ${this.index.slug}`);
        }
        return config;
    }
    get routes() {
        return this.config.routes;
    }
    get integration() {
        return this.index.integration;
    }
    getStreamId() {
        return this.index.stream?.id?.toString();
    }
    get recentlyPlayedEnabled() {
        return !!this.index.stream?.recentlyPlayedEnabled;
    }
    getPrimaryDomain() {
        // Get primary domain for local station
        return this.index.canonicalHostname;
    }
    getShareUrl(request, path) {
        return `${request.protocol.replace(":", "")}://${this.getPrimaryDomain()}${path || request.path}`;
    }
    getMicrosite(brand) {
        return this.micrositeReferences.find(({ ownBrand }) => ownBrand === brand);
    }
}
export class Microsite {
    constructor(microsite) {
        Object.defineProperty(this, "microsite", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: microsite
        });
        if (!this.config.sections) {
            throw new Error("No Microsite Config");
        }
    }
    get sections() {
        if (!this.config.sections) {
            throw new Error("No Microsite Config");
        }
        return this.config.sections;
    }
    get index() {
        return this.microsite.sites.find?.inboundRef;
    }
    get config() {
        const config = this.index?.configByLookup;
        if (!config) {
            log.debug(`No Config Record for slug: ${this.index?.slug}`);
            throw new Error(`No Config Record on Index: ${this.index?._id} for slug: ${this.index?.slug}`);
        }
        return config;
    }
    getPrimaryDomain() {
        return this.index?.inboundKeys?.canonicalHostname || "";
    }
    getShareUrl(request) {
        const primaryDomain = this.getPrimaryDomain();
        if (primaryDomain) {
            return `${request.protocol.replace(":", "")}://${primaryDomain}${request.path}`;
        }
        return "";
    }
}
//# sourceMappingURL=Sites.types.js.map